import { useMutation } from '@apollo/client';
import { Callout, Flex, Select, Switch, Text, TextField } from '@radix-ui/themes';
import { Button } from '@hubertai/hubert-component-library';
import { Form } from 'radix-ui';
import { use } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CountryFlag, countryCodeForAppLanguage } from '../../utils/languageFlag';
import LocaleContext from '../hooks/LocaleContext';
import { denyConsent, getConsentStatus, grantConsent, useConsentStatus } from '../../analytics/consent';
import { HUBERT_VIOLET } from '../consent/brandColors';
import { EDIT_USER_INFO } from './queries';

type UserProfileState = {
  firstName: string;
  lastName: string;
  phone: string;
  emailProductUpdates: boolean;
  language?: string;
  analyticsConsent: boolean;
};

type SubmitData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  emailProductUpdates: string;
  language: string;
  analyticsConsent?: string;
};

const UserProfile = () => {
  const { t, i18n } = useTranslation();
  const [editUserInfo, { error, loading }] = useMutation(EDIT_USER_INFO);
  const { userSettings, setUserVariables } = use(LocaleContext);

  const handleChangeUser = (values: UserProfileState) => {
    if (values.language && values.language !== i18n.language) {
      i18n.changeLanguage(values.language);
    }

    // Analytics consent is saved with the rest of the form. Saving without touching the switch
    // records nothing, so someone who never chose isn't counted as having declined.
    const granted = getConsentStatus() === 'granted';
    if (values.analyticsConsent && !granted) grantConsent('profile');
    if (!values.analyticsConsent && granted) denyConsent();

    editUserInfo({
      variables: {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        emailProductUpdates: values.emailProductUpdates,
      },
    })
      .then(() => {
        toast.success(t('settings.userProfile.toast.success'));
      })
      .catch(() => {
        toast.error(t('settings.userProfile.toast.error'));
      });

    setUserVariables?.({
      user: {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        settings: {
          emailProductUpdates: values.emailProductUpdates,
        },
        ...userSettings,
      },
    });
  };
  const userLoaded = userSettings.id != null;
  return (
    <div className="border border-gray-200 rounded p-4">
      <h2 className="truncate pb-4">{t('settings.userProfile.heading')}</h2>
      {userLoaded && (
        <ChangeInfo state={userSettings} handleSubmit={handleChangeUser} response={{ error }} loading={loading} />
      )}
      {!userLoaded && t('settings.userProfile.loading')}
    </div>
  );
};

export default UserProfile;

const ChangeInfo = ({
  state,
  handleSubmit,
  response,
  loading,
}: {
  state: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    settings?: { emailProductUpdates?: boolean };
  };
  handleSubmit: (values: UserProfileState) => void;
  response: { error?: { message: string } };
  loading: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const consentStatus = useConsentStatus();
  return (
    <>
      {response.error && response.error.message && (
        <Callout.Root color="red">
          <Callout.Text className="mb-0">{response.error.message}</Callout.Text>
        </Callout.Root>
      )}
      <Form.Root
        className="w-1/2"
        onSubmit={(formEvent) => {
          formEvent.preventDefault();

          const form = formEvent.currentTarget;
          const formData = Object.fromEntries(new FormData(form)) as unknown as SubmitData;

          handleSubmit({
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            phone: formData.phone.trim(),
            emailProductUpdates: formData.emailProductUpdates === 'on' ? true : false,
            language: formData.language,
            analyticsConsent: formData.analyticsConsent === 'on',
          });
        }}
      >
        <Form.Field name="firstName" className="mb-4">
          <div className="flex items-baseline justify-between">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('settings.userProfile.form.firstNameLabel')}</strong>
              </Text>
            </Form.Label>
            <Text size="1" color="red">
              <Form.Message match="valueMissing">{t('settings.userProfile.form.firstNameRequired')}</Form.Message>
            </Text>
          </div>
          <Form.Control asChild>
            <TextField.Root
              placeholder={t('settings.userProfile.form.firstNamePlaceholder')}
              type="text"
              required
              className="w-full"
              size="3"
              defaultValue={state.firstName}
            />
          </Form.Control>
        </Form.Field>
        <Form.Field name="lastName" className="mb-4">
          <div className="flex items-baseline justify-between">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('settings.userProfile.form.lastNameLabel')}</strong>
              </Text>
            </Form.Label>
            <Text size="1" color="red">
              <Form.Message match="valueMissing">{t('settings.userProfile.form.lastNameRequired')}</Form.Message>
            </Text>
          </div>
          <Form.Control asChild>
            <TextField.Root
              placeholder={t('settings.userProfile.form.lastNamePlaceholder')}
              type="text"
              required
              className="w-full"
              size="3"
              defaultValue={state.lastName}
            />
          </Form.Control>
        </Form.Field>
        <Form.Field name="phone" className="mb-4">
          <div className="flex items-baseline justify-between">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('settings.userProfile.form.phoneLabel')}</strong>{' '}
                {t('settings.userProfile.form.phoneOptionalSuffix')}
              </Text>
            </Form.Label>
            <Text size="1" color="red">
              <Form.Message match="patternMismatch">{t('settings.userProfile.form.phoneInvalid')}</Form.Message>
            </Text>
          </div>
          <Form.Control asChild>
            <TextField.Root
              placeholder={t('settings.userProfile.form.phonePlaceholder')}
              type="tel"
              className="w-full"
              size="3"
              pattern="\+[0-9](\s*[0-9]\s*){4,}"
              defaultValue={state.phone}
            />
          </Form.Control>
        </Form.Field>
        <Form.Field name="email" className="mb-4">
          <div className="flex items-baseline justify-between">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('settings.userProfile.form.emailLabel')}</strong>
              </Text>
            </Form.Label>
            <Text size="1" color="red">
              <Form.Message match="valueMissing">{t('settings.userProfile.form.emailRequired')}</Form.Message>
              <Form.Message match="typeMismatch">{t('settings.userProfile.form.emailInvalid')}</Form.Message>
            </Text>
          </div>
          <TextField.Root
            placeholder={t('settings.userProfile.form.emailPlaceholder')}
            type="email"
            required
            disabled
            className="w-full"
            size="3"
            defaultValue={state.email}
          />
        </Form.Field>
        <Form.Field name="language" className="mb-6">
          <div className="flex items-baseline justify-between mb-2">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('nav.languageGlobe.label')}</strong>
              </Text>
            </Form.Label>
          </div>
          <Select.Root defaultValue={i18n.resolvedLanguage || i18n.language || 'en'} name="language">
            <Select.Trigger className="w-full" />
            <Select.Content>
              <Select.Item value="en">
                <Flex as="span" align="center" gap="1" display="inline-flex">
                  <CountryFlag countryCode={countryCodeForAppLanguage('en')} />
                  English
                </Flex>
              </Select.Item>
              <Select.Item value="sv">
                <Flex as="span" align="center" gap="1" display="inline-flex">
                  <CountryFlag countryCode={countryCodeForAppLanguage('sv')} />
                  Svenska
                </Flex>
              </Select.Item>
              <Select.Item value="fr">
                <Flex as="span" align="center" gap="1" display="inline-flex">
                  <CountryFlag countryCode={countryCodeForAppLanguage('fr')} />
                  Français
                </Flex>
              </Select.Item>
              <Select.Item value="es">
                <Flex as="span" align="center" gap="1" display="inline-flex">
                  <CountryFlag countryCode={countryCodeForAppLanguage('es')} />
                  Español
                </Flex>
              </Select.Item>
              <Select.Item value="de">
                <Flex as="span" align="center" gap="1" display="inline-flex">
                  <CountryFlag countryCode={countryCodeForAppLanguage('de')} />
                  Deutsch
                </Flex>
              </Select.Item>
            </Select.Content>
          </Select.Root>
        </Form.Field>
        <Form.Field name="emailProductUpdates" className="mb-6">
          <div className="flex items-baseline justify-between mb-2">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('settings.userProfile.form.communicationPreferencesLabel')}</strong>
              </Text>
            </Form.Label>
          </div>
          <div className="flex flex-row gap-2 items-center">
            <Form.Control asChild>
              <Switch
                size="1"
                className="pr-2"
                style={HUBERT_VIOLET}
                defaultChecked={state.settings?.emailProductUpdates}
              />
            </Form.Control>
            <Form.Label asChild>
              <Text as="label" size="2" className="m-0">
                {t('settings.userProfile.form.productUpdatesToggleLabel')}
              </Text>
            </Form.Label>
          </div>
        </Form.Field>
        <Form.Field name="analyticsConsent" className="mb-6">
          <div className="flex items-baseline justify-between mb-2">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('analyticsConsent.settingHeading')}</strong>
              </Text>
            </Form.Label>
          </div>
          <div className="flex flex-row gap-2 items-center">
            <Form.Control asChild>
              <Switch size="1" className="pr-2" style={HUBERT_VIOLET} defaultChecked={consentStatus === 'granted'} />
            </Form.Control>
            <Form.Label asChild>
              <Text as="label" size="2" className="m-0">
                {t('analyticsConsent.settingLabel')}
              </Text>
            </Form.Label>
          </div>
        </Form.Field>
        <Form.Submit asChild>
          <Button size="3" color="grass" variant="solid" className="w-1/2!" disabled={loading} loading={loading}>
            {t('settings.userProfile.saveButton')}
          </Button>
        </Form.Submit>
      </Form.Root>
    </>
  );
};
